import { providers } from 'ethers';

import { Contracts } from './contracts';
import { addressesFormat, required } from '../contract-methods/decorators.methods';
import { getTransactionOptions, loadContractWithCaller, TransactionOptionsInterface } from '../utils/utils';
import {
  addTrustedIssuer,
  getTrustedIssuerClaimTopics,
  getTrustedIssuers,
  hasClaimTopic,
  isTrustedIssuer,
  removeTrustedIssuer,
  updateIssuerClaimTopics,
} from '../contract-methods/trustedIssuersRegistry.methods';
import {
  batchRegisterIdentities,
  contains,
  deleteIdentity,
  isVerified,
  registerIdentity,
  updateCountry,
  updateIdentity,
} from '../contract-methods/identityRegistry.methods';
import { Role } from '../utils/enum';
import { addClaimTopic, getClaimTopics, removeClaimTopic } from '../contract-methods/claimTopicsRegistry.methods';
import {
  bindIdentityRegistry,
  getLinkedIdentityRegistries,
  getStoredIdentity,
  getStoredInvestorCountry,
  unbindIdentityRegistry,
} from '../contract-methods/identityRegistryStorage.methods';

type TransactionResponse = providers.TransactionResponse;

export class Registry extends Contracts {
  // IDENTITY REGISTRY

  /**
   * Register an Identity on the Identity Registry
   * @returns {Promise<TransactionResponse>}
   * @param tx
   * @param blockchainOptions
   */
  @addressesFormat()
  public async registerIdentityOfWallet(
    @required
    tx: {
      address: string;
      identityAddress: string;
      country: number;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await registerIdentity({
      country: tx.country,
      identityAddress: tx.identityAddress,
      userAddress: tx.address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Registers identities in batch
   * @param {string[]} userAddresses
   * @param {string[]} identityAddresses
   * @param {number[]} countries
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchRegisterIdentities(
    @required
    {
      userAddresses,
      identityAddresses,
      countries,
    }: {
      userAddresses: string[];
      identityAddresses: string[];
      countries: number[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchRegisterIdentities({
      userAddresses,
      identityAddresses,
      countries,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Updates Identity (OnchainID) address from the Identity Registry
   * @param {{address: string; identityAddress: string}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async updateIdentityOfWallet(
    @required
    tx: {
      address: string;
      identityAddress: string;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await updateIdentity({
      userAddress: tx.address,
      identity: tx.identityAddress,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Updates the country corresponding to the wallet's address
   * @param {{address: string; country: number}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async updateCountryOfWallet(
    @required
    tx: {
      address: string;
      country: number;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await updateCountry({
      wallet: tx.address,
      country: tx.country,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes an Identity from the IdentityRegistry
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async unregisterIdentityOfWallet(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await deleteIdentity({
      wallet: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Checks if the wallet is Registered into the IdentityRegistry
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isWalletRegistered(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await contains({
      address: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    });
  }

  /**
   * Checks if the Wallet is compliant
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isIdentityOfWalletCompliant(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await isVerified({
      wallet: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    });
  }

  /**
   * Returns the Identity of the wallet's address
   * @param {string} address
   * @returns {Promise<string>}
   */
  @addressesFormat()
  public async getIdentityAddress(@required address: string): Promise<string> {
    return await this.identityRegistry.identity(address);
  }

  /**
   * Returns the Country of the wallet's address
   * @param {string} address
   * @returns {Promise<number>}
   */
  @addressesFormat()
  public async getIdentityCountry(@required address: string): Promise<number> {
    return await this.identityRegistry.investorCountry(address);
  }

  /**
   * Returns all the claims for which the Issuer is trusted
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<number[]>}
   */
  @addressesFormat()
  public async getTrustedIssuerClaimTopics(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<number[]> {
    return await getTrustedIssuerClaimTopics({
      issuerAddress: address,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry),
    });
  }

  // IDENTITY REGISTRY STORAGE

  /**
   * Returns all Linked Identity Registries
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string[]>}
   */
  public async getLinkedIdentityRegistries(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string[]> {
    return await getLinkedIdentityRegistries({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
    });
  }

  /**
   * Returns stored investor Identity from the IdentityStorage
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string>}
   */
  @addressesFormat()
  public async getStoredIdentity(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getStoredIdentity({
      address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
    });
  }

  /**
   * Returns stored investor Country from the IdentityStorage
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<number>}
   */
  @addressesFormat()
  public async getStoredInvestorCountry(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<number> {
    return await getStoredInvestorCountry({
      address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
    });
  }

  /**
   * Adds the identity registry to the list of identityRegistries linked to the storage contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async bindIdentityRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await bindIdentityRegistry({
      address: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes the identity registry to the list of identityRegistries linked to the storage contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async unbindIdentityRegistry(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await unbindIdentityRegistry({
      address: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  // TRUSTED ISSUERS REGISTRY

  /**
   * Adds an Issuer as Trusted into the TrustedIssuersRegistry
   * @param {{address: string; claimTopics: number[]}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async trustClaimIssuer(
    @required
    tx: {
      address: string;
      claimTopics: number[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await addTrustedIssuer({
      trustedIssuer: tx.address,
      claimTopics: tx.claimTopics,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry, Role.TrustedIssuersOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes an Issuer from the TrustedIssuersRegistry
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async untrustClaimIssuer(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await removeTrustedIssuer({
      trustedIssuer: address,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry, Role.TrustedIssuersOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Updates the set of claim topics that a trusted issuer is allowed to emit
   * @param {{index: number; address: string; claimTopics: number[]}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async updateIssuerClaimTopics(
    @required
    tx: {
      address: string;
      claimTopics: number[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await updateIssuerClaimTopics({
      claimIssuer: tx.address,
      claimTopics: tx.claimTopics,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry, Role.TrustedIssuersOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Returns if a ClaimIssuer is trusted for a specific topic
   * @param {{address: string; claimTopic: number}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isClaimIssuerTrustedForTopic(
    @required
    tx: {
      address: string;
      claimTopic: number;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await hasClaimTopic({
      issuerAddress: tx.address,
      claimTopic: tx.claimTopic,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry),
    });
  }

  /**
   * Returns the list of all TrustedIssuers
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string[]>}
   */
  public async getTrustedIssuers(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string[]> {
    return await getTrustedIssuers({
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry),
    });
  }

  /**
   * Checks if an Issuer is Trusted or not
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isClaimIssuerTrusted(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await isTrustedIssuer({
      issuerAddress: address,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry),
    });
  }

  // CLAIM TOPICS REGISTRY

  /**
   * Adds a required Claim Topics into the ClaimTopicsRegistry
   * @param {number} claimTopic
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async addRequiredClaimTopic(
    claimTopic: number,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await addClaimTopic({
      claimTopic,
      registry: await loadContractWithCaller(blockchainOptions, this.claimTopicsRegistry, Role.ClaimTopicsOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes a required Claim Topics from the ClaimTopicsRegistry
   * @param {number} claimTopic
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async removeRequiredClaimTopic(
    claimTopic: number,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await removeClaimTopic({
      claimTopic,
      registry: await loadContractWithCaller(blockchainOptions, this.claimTopicsRegistry, Role.ClaimTopicsOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Returns all required claims
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<number[]>}
   */
  public async getRequiredClaimTopics(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<number[]> {
    return await getClaimTopics({
      registry: await loadContractWithCaller(blockchainOptions, this._claimTopicsRegistry),
    });
  }
}
